// Runtime brand color -> CSS custom properties.
//
// Always applied: main.ts starts with DISTRIBUTION.primaryColor and App.vue
// switches to the tenant's branding color once the user is known. Light mode
// uses the color as-is; dark mode gets a lighter variant of the Ionic primary
// family (see buildBrandDarkVars / applyBrandColor).

type RGB = { r: number; g: number; b: number };

// API branding.color is a 6-digit hex (App\Models\Tenant::getBrandColor()).
const HEX = /^#([0-9a-f]{6})$/i;

const WHITE: RGB = { r: 255, g: 255, b: 255 };
const BLACK: RGB = { r: 0, g: 0, b: 0 };

// White-mix weights for the light end (50..400) and black-mix for the dark end
// (600..950); 500 is the base color itself.
const LIGHT_STEPS: Record<number, number> = { 50: 0.92, 100: 0.82, 200: 0.62, 300: 0.42, 400: 0.2 };
const DARK_STEPS: Record<number, number> = { 600: 0.12, 700: 0.28, 800: 0.44, 900: 0.6, 950: 0.74 };

// The full set of custom properties we manage, so we can cleanly remove them.
export const BRAND_VAR_KEYS = [
  // Bare Tailwind token: `bg-primary` / `text-primary` / `border-primary` compile
  // to `var(--color-primary)`, so this one drives most of the app's surfaces.
  '--color-primary',
  '--color-primary-50', '--color-primary-100', '--color-primary-200', '--color-primary-300',
  '--color-primary-400', '--color-primary-500', '--color-primary-600', '--color-primary-700',
  '--color-primary-800', '--color-primary-900', '--color-primary-950',
  '--ion-color-primary', '--ion-color-primary-rgb', '--ion-color-primary-shade',
  '--ion-color-primary-tint', '--ion-color-primary-contrast', '--ion-color-primary-contrast-rgb',
] as const;

function parseHex(hex: string): RGB | null {
  const m = HEX.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex({ r, g, b }: RGB): string {
  const h = (v: number) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function mix(c: RGB, t: RGB, w: number): RGB {
  return { r: c.r + (t.r - c.r) * w, g: c.g + (t.g - c.g) * w, b: c.b + (t.b - c.b) * w };
}

// Perceived luminance (sRGB approximation) to pick a readable contrast color.
function luminance({ r, g, b }: RGB): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// WCAG 2 relative luminance and contrast ratio.
function relativeLuminance({ r, g, b }: RGB): number {
  const ch = (v: number) => {
    const c = Math.round(v) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

function contrastRatio(a: RGB, b: RGB): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// Dark-mode page background (--ion-background-color under .dark-mode) and the
// WCAG AA minimum for body text on it.
const DARK_BG: RGB = { r: 0x0a, g: 0x0a, b: 0x0f };
const DARK_MIN_CONTRAST = 4.5;

function ionPrimaryVars(c: RGB): Record<string, string> {
  const contrastIsDark = luminance(c) > 0.6;
  return {
    '--ion-color-primary': toHex(c),
    '--ion-color-primary-rgb': `${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}`,
    '--ion-color-primary-shade': toHex(mix(c, BLACK, 0.12)),
    '--ion-color-primary-tint': toHex(mix(c, WHITE, 0.1)),
    '--ion-color-primary-contrast': contrastIsDark ? '#000000' : '#ffffff',
    '--ion-color-primary-contrast-rgb': contrastIsDark ? '0, 0, 0' : '255, 255, 255',
  };
}

/**
 * Build the CSS custom properties for a tenant brand color, or null when the
 * color is missing / not a 6-digit hex.
 */
export function buildBrandVars(hex: string | null | undefined): Record<string, string> | null {
  if (!hex) return null;
  const base = parseHex(hex);
  if (!base) return null;

  const vars: Record<string, string> = {};

  // App scale --color-primary-50..950 (500 = base) plus the bare `--color-primary`
  // token that Tailwind's `primary` colour resolves to.
  vars['--color-primary'] = toHex(base);
  vars['--color-primary-500'] = toHex(base);
  for (const [step, w] of Object.entries(LIGHT_STEPS)) vars[`--color-primary-${step}`] = toHex(mix(base, WHITE, w));
  for (const [step, w] of Object.entries(DARK_STEPS)) vars[`--color-primary-${step}`] = toHex(mix(base, BLACK, w));

  // Ionic primary vars (mirror the structure in theme/variables.scss).
  Object.assign(vars, ionPrimaryVars(base));

  return vars;
}

/**
 * The dark-mode counterpart of the Ionic primary family: the brand color mixed
 * towards white just far enough to reach 4.5:1 on the dark background. Not
 * further, because 31 surfaces use `--ion-color-primary` as a background under
 * white text; at the threshold white on it still sits around 4.4:1.
 *
 * Only the `--ion-color-primary*` family, the same set `.dark-mode` in
 * theme/variables.scss swaps. `--color-primary` (Tailwind `text-/bg-primary`)
 * stays as it is in both modes: it carries white text on ~47 `bg-primary`
 * surfaces, which a lighter shade would make unreadable.
 */
export function buildBrandDarkVars(hex: string | null | undefined): Record<string, string> | null {
  if (!hex) return null;
  const base = parseHex(hex);
  if (!base) return null;

  let c = base;
  for (let w = 0.02; contrastRatio(c, DARK_BG) < DARK_MIN_CONTRAST && w <= 1; w += 0.02) {
    c = mix(base, WHITE, w);
  }
  return ionPrimaryVars(c);
}

/**
 * Read the tenant brand color out of the cached user JSON (Capacitor
 * Preferences `auth_user`). Returns null when absent or unparseable. Used to
 * apply the brand color synchronously at boot, before the user payload resolves.
 */
export function brandColorFromCachedUser(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const user = JSON.parse(value);
    const color = user?.branding?.color;
    return typeof color === 'string' && color ? color : null;
  } catch {
    return null;
  }
}

/**
 * Read a resolved brand CSS custom property (e.g. '--ion-color-primary',
 * '--color-primary-300') for JS contexts that need a real color string and
 * cannot reference a CSS var directly (e.g. Chart.js canvas datasets). Falls
 * back to the given default when unavailable (SSR / not yet applied).
 */
export function readBrandVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

const DARK_STYLE_ID = 'brand-theme-dark';

/**
 * Apply (or clear) the tenant brand color.
 *
 * The light set goes inline on <html>. `.dark-mode` also sits on <html>, so an
 * inline var beats the lighter primary from the `.dark-mode` block in
 * theme/variables.scss, and dark mode ended up with the light-mode brand color
 * on a near-black background. The dark set therefore goes in a <style> rule
 * with `!important`: an important author declaration outranks a normal inline
 * one, and the rule only matches while `.dark-mode` is on.
 *
 * Passing a null/invalid color removes both (back to the compiled defaults).
 */
export function applyBrandColor(hex: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const vars = buildBrandVars(hex);
  const darkVars = buildBrandDarkVars(hex);

  if (!vars || !darkVars) {
    BRAND_VAR_KEYS.forEach((k) => root.style.removeProperty(k));
    document.getElementById(DARK_STYLE_ID)?.remove();
    return;
  }

  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));

  let style = document.getElementById(DARK_STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = DARK_STYLE_ID;
    document.head.appendChild(style);
  }
  const body = Object.entries(darkVars).map(([k, v]) => `  ${k}: ${v} !important;`).join('\n');
  style.textContent = `:root.dark-mode {\n${body}\n}`;
}

/** Apply the fixed distribution accent used by signed-out brand surfaces. */
export function applyDistributionAccent(hex: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const color = hex ? parseHex(hex) : null;

  if (!color) {
    root.style.removeProperty('--distribution-accent');
    root.style.removeProperty('--distribution-accent-rgb');
    return;
  }

  root.style.setProperty('--distribution-accent', toHex(color));
  root.style.setProperty('--distribution-accent-rgb', `${color.r}, ${color.g}, ${color.b}`);
}
